import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Select, Button, Table, Badge, List, Grid, Typography, Input, Tag, Modal, Spin } from 'antd';
import { ExportOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons';
import { useFindAllEntityMasterMutation } from '../../redux-toolkit/services/entityMasterService';
import { useGetDashboardDataMutation } from '../../redux-toolkit/services/dashboardService';
import { useGetAllAssetsMutation, useGetAllAssetsBySearchMutation } from '../../redux-toolkit/services/assetsService';
import { useFindAllAssetTypeMutation } from '../../redux-toolkit/services/assetTypeService';
import { getLocalStorageData } from '../../utils/LocalStorageService';
import './dashboard.scss';

const { Option } = Select;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const WealthStabilityDashboard = () => {
    const screens = useBreakpoint();
    const [selectedEntity, setSelectedEntity] = useState<string | null>(null);
    const [entities, setEntities] = useState<any[]>([]);
    const [assetTypes, setAssetTypes] = useState<any[]>([]);

    // Local filters
    const [searchText, setSearchText] = useState('');
    const [assetTypeFilter, setAssetTypeFilter] = useState('All Asset Types');

    const localUserObj: any = getLocalStorageData("localUser");
    const token = localUserObj?.token;

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Modal State
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedAsset, setSelectedAsset] = useState<any>(null);

    const showModal = (record: any) => {
        setSelectedAsset(record);
        setIsModalVisible(true);
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
        setSelectedAsset(null);
    };

    // API Hooks
    const [findAllEntities, { data: allEntitiesData, isLoading: isEntitiesLoading }] = useFindAllEntityMasterMutation();
    const [getDashboardData, { data: dashboardData, isLoading: isDashboardLoading }] = useGetDashboardDataMutation();
    const [getAllAssets, { data: assetsData, isLoading: isAssetsLoading }] = useGetAllAssetsMutation();
    const [getAllAssetsBySearch, { data: assetsSearchData, isLoading: isAssetsSearchLoading }] = useGetAllAssetsBySearchMutation();
    const [findAllAssetType, { data: allAssetTypesData }] = useFindAllAssetTypeMutation();

    useEffect(() => {
        if (token) {
            findAllEntities({ token });
            findAllAssetType({ token });
        }
    }, [token]);

    useEffect(() => {
        if (allEntitiesData?.data) {
            setEntities(allEntitiesData.data);
            if (!selectedEntity && allEntitiesData.data.length > 0) {
                setSelectedEntity(allEntitiesData.data[0].id);
            }
        }
    }, [allEntitiesData]);

    useEffect(() => {
        if (allAssetTypesData) {
            // Depending on response structure, could be allAssetTypesData directly or allAssetTypesData.data
            const types = Array.isArray(allAssetTypesData) ? allAssetTypesData : allAssetTypesData.data || [];
            setAssetTypes(types);
        }
    }, [allAssetTypesData]);

    useEffect(() => {
        if (selectedEntity && token) {
            getDashboardData({ payload: { entityId: selectedEntity }, token });
        }
    }, [selectedEntity, token]);

    useEffect(() => {
        if (!token) return;

        if (!searchText && assetTypeFilter === 'All Asset Types') {
            getAllAssets({
                token,
                page: currentPage - 1,
                size: pageSize,
                sort: "desc",
            });
        } else {
            getAllAssetsBySearch({
                token,
                payload: {
                    offset: pageSize,
                    pageNumber: currentPage - 1,
                    searchBy: searchText,
                    assetType: assetTypeFilter === 'All Asset Types' ? '' : assetTypeFilter
                },
            });
        }
    }, [token, currentPage, pageSize, searchText, assetTypeFilter]);

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(e.target.value);
        setCurrentPage(1); // Reset pagination on search
    };

    const handleAssetTypeChange = (value: string) => {
        setAssetTypeFilter(value);
        setCurrentPage(1); // Reset pagination on filter
    };

    const handleEntityChange = (value: string) => {
        setSelectedEntity(value);
    };

    // Derived Data
    const currentEntity = entities.find(e => e.id === selectedEntity);
    const entityName = currentEntity?.entityName || 'Select Entity';

    // -- Data for Assets --
    const isSearching = !!searchText || assetTypeFilter !== 'All Asset Types';
    const apiData = isSearching ? assetsSearchData?.data : assetsData?.data;
    const totalRecords = isSearching ? (assetsSearchData?.totalRecords || 0) : (assetsData?.totalRecords || 0);

    const tableData = Array.isArray(apiData) ? apiData.map((item: any) => ({
        key: item.id,
        asset: item.name || '-',
        entity: item.entityMaster?.name || '-',
        type: item.assetType?.name || '-',
        value: item.current ? (item.current / 10000000) : 0,
        liquidity: 'Medium',  // Not present on API schema explicitly
        health: item.healthScore || 0
    })) : [];

    // Local filtering removed since API handles it now. Using tableData directly.
    const filteredAssets = tableData;

    // Derived Metrics
    const totalAssetValue = filteredAssets.reduce((sum, item) => sum + (Number(item.value) || 0), 0);
    const validHealthAssets = filteredAssets.filter(item => item.health && typeof item.health === 'number');
    const avgHealth = validHealthAssets.length > 0
        ? (validHealthAssets.reduce((sum, item) => sum + item.health, 0) / validHealthAssets.length).toFixed(1)
        : "0.0";
    const highLiquidityCount = filteredAssets.filter(item => item.liquidity === 'High').length;

    const formattedTotalAssetValue = `₹${totalAssetValue.toLocaleString('en-IN', { maximumFractionDigits: 2 })}cr`;

    const renderHealthBadge = (score: number) => {
        let color = '#52c41a'; // 5
        if (score === 4) color = '#1890ff';
        if (score === 3) color = '#faad14';
        if (score < 3) color = '#f5222d';
        return (
            <div style={{
                width: '28px', height: '28px', borderRadius: '50%', backgroundColor: color,
                color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 'bold', margin: '0 auto'
            }}>
                {score}
            </div>
        );
    };

    const renderLiquidityTag = (level: string) => {
        let color = 'default';
        if (level === 'High') color = 'success';
        if (level === 'Medium') color = 'warning';
        if (level === 'Low') color = 'error';
        return <Tag color={color} style={{ minWidth: '60px', textAlign: 'center' }}>{level}</Tag>;
    };

    // Sidebar Mock Data
    const alerts = [
        { id: 1, text: 'Project at risk: Retail OmniPilot (Isha Ambani) — requires attention.', type: 'warning' },
        { id: 2, text: 'Project at risk: Green Hydrogen Pilot (Anant Ambani) — requires attention.', type: 'warning' },
    ];
    const quickStats = [
        { label: 'Heirs', value: 5, color: '#e6f7ff', textColor: '#1890ff' },
        { label: 'Assets', value: 4, color: '#f6ffed', textColor: '#52c41a' },
        { label: 'Projects', value: 3, color: '#fff7e6', textColor: '#fa8c16' },
        { label: 'Audits', value: 3, color: '#fff1f0', textColor: '#f5222d' },
    ];

    return (
        <div className="p-4" style={{ backgroundColor: '#f0f2f5', minHeight: '100vh' }}>
            {/* Header Section */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <Title level={2} style={{ marginBottom: 0 }}>
                        {entityName} — Reliance Family Office
                    </Title>
                    <Text type="secondary">Single master dashboard • Succession • Wealth • Projects • CAP Table • Audits • Mentorship</Text>
                    <div style={{ marginTop: '8px' }} className="d-flex align-items-center text-muted">
                        <span>Dashboard</span> <RightOutlined style={{ fontSize: '10px', margin: '0 8px' }} /> <span className="text-primary">Wealth</span>
                    </div>
                </div>
                {/* <div className="d-flex gap-3">
                    <Select
                        showSearch
                        placeholder="Search across dashboard..."
                        optionFilterProp="children"
                        onChange={handleEntityChange}
                        onSearch={(val) => console.log('search:', val)}
                        filterOption={(input, option) =>
                            (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())
                        }
                        style={{ width: 300 }}
                        value={selectedEntity}
                        loading={isEntitiesLoading}
                    >
                        {entities.map(e => (
                            <Option key={e.id} value={e.id}>{e.entityName}</Option>
                        ))}
                    </Select>
                    <Button type="primary" icon={<ExportOutlined />}>Export / Print</Button>
                </div> */}
            </div>

            {/* Main Content */}
            <Row gutter={[24, 24]}>
                {/* Left Column - Asset Table */}
                <Col span={24}>
                    <Card className="mb-4 shadow-sm" bordered={false} bodyStyle={{ padding: '24px' }}>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <Title level={4} className="mb-0">Wealth Stability & Entity Exposure</Title>
                            <div className="d-flex gap-2">
                                <Input
                                    prefix={<SearchOutlined />}
                                    placeholder="Search asset..."
                                    style={{ width: 200 }}
                                    onChange={handleSearch}
                                />
                                <Select value={assetTypeFilter} onChange={handleAssetTypeChange} style={{ width: 150 }}>
                                    <Option value="All Asset Types">All Asset Types</Option>
                                    {assetTypes.map(type => (
                                        <Option key={type.id || type.name} value={type.name}>
                                            {type.name}
                                        </Option>
                                    ))}
                                </Select>
                            </div>
                        </div>

                        <Text type="secondary" className="mb-3 d-block">Showing {filteredAssets.length} of {totalRecords} assets</Text>

                        <Table
                            dataSource={filteredAssets}
                            pagination={{
                                total: totalRecords,
                                current: currentPage,
                                pageSize: pageSize,
                                onChange: (page, size) => {
                                    setCurrentPage(page);
                                    setPageSize(size);
                                }
                            }}
                            loading={isAssetsLoading || isAssetsSearchLoading}
                            columns={[
                                { title: 'ASSET', dataIndex: 'asset', key: 'asset', render: (text) => <b>{text}</b> },
                                { title: 'ENTITY', dataIndex: 'entity', key: 'entity', render: (text) => <Text type="secondary">{text}</Text> },
                                { title: 'TYPE', dataIndex: 'type', key: 'type' },
                                { title: 'VALUE (₹CR)', dataIndex: 'value', key: 'value', render: (text) => <b>{Number(text).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</b> },
                                { title: 'LIQUIDITY', dataIndex: 'liquidity', key: 'liquidity', align: 'center', render: (text) => renderLiquidityTag(text) },
                                { title: 'HEALTH', dataIndex: 'health', key: 'health', align: 'center', render: (score) => renderHealthBadge(score) },
                                { title: 'ACTION', key: 'action', align: 'center', render: (_, record) => <a href="#" onClick={(e) => { e.preventDefault(); showModal(record); }}>View</a> }
                            ]}
                        />

                        {/* KPI Summaries below table */}
                        <Row gutter={16} className="mt-4">
                            <Col span={8}>
                                <div className="p-3 rounded" style={{ backgroundColor: '#f0f5ff' }}>
                                    <Text type="secondary" className="d-block mb-1">Total Asset Value</Text>
                                    <Title level={3} className="text-primary m-0">{formattedTotalAssetValue}</Title>
                                </div>
                            </Col>
                            <Col span={8}>
                                <div className="p-3 rounded" style={{ backgroundColor: '#f6ffed' }}>
                                    <Text type="secondary" className="d-block mb-1">Average Health</Text>
                                    <Title level={3} className="text-success m-0">{avgHealth} / 5</Title>
                                </div>
                            </Col>
                            <Col span={8}>
                                <div className="p-3 rounded" style={{ backgroundColor: '#fff0f6' }}>
                                    <Text type="secondary" className="d-block mb-1">High Liquidity Assets</Text>
                                    <Title level={3} style={{ color: '#eb2f96', margin: 0 }}>{highLiquidityCount}</Title>
                                </div>
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>

            {/* Asset Details Modal */}
            <Modal
                title={
                    <span style={{ fontSize: '18px', fontWeight: 'bold' }}>
                        {`${selectedAsset?.asset || 'Asset'} Details`}
                    </span>
                }
                open={isModalVisible}
                onCancel={handleModalClose}
                footer={[
                    <Button key="close" onClick={handleModalClose} style={{ backgroundColor: '#e2e8f0', borderColor: '#e2e8f0', color: '#1e293b' }}>
                        Close
                    </Button>,
                    <Button key="action" type="primary" onClick={() => console.log('Take action clicked')} style={{ backgroundColor: '#2563eb' }}>
                        Take Action
                    </Button>,
                ]}
                centered
            >
                {selectedAsset && (
                    <div style={{ fontSize: '15px', marginTop: '16px', marginBottom: '16px', color: '#475569' }}>
                        <p style={{ margin: '8px 0' }}>Entity: {selectedAsset.entity}</p>
                        <p style={{ margin: '8px 0' }}>Type: {selectedAsset.type}</p>
                        <p style={{ margin: '8px 0' }}>Value: ₹{Number(selectedAsset.value).toLocaleString('en-IN', { maximumFractionDigits: 2 })}cr</p>
                        <p style={{ margin: '8px 0' }}>Liquidity: {selectedAsset.liquidity}</p>
                        <p style={{ margin: '8px 0' }}>Health Score: {selectedAsset.health}/5</p>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default WealthStabilityDashboard;
